import React, { useState, useEffect, useCallback } from 'react';
import { useGrammarCards } from '../../hooks/useGrammarCards';
import { GrammarCard, StudySession } from '../../types/grammar';
import './Flashcards.css';

interface GrammarFlashcardsProps {
  onNavigateHome: () => void;
}

const GrammarFlashcards: React.FC<GrammarFlashcardsProps> = ({ onNavigateHome }) => {
  const { 
    getCardsByGradeLevel,
    getGradeLevels,
    getCategoriesForGrade,
    loading, 
    error, 
    updateCardStatistics,
    overallStats,
    createStudySession,
    updateStudySession,
    endStudySession,
    isReady
  } = useGrammarCards();

  // State management
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
  const [currentCards, setCurrentCards] = useState<GrammarCard[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showingAnswer, setShowingAnswer] = useState(false);
  const [showingMoreInfo, setShowingMoreInfo] = useState(false);
  const [points, setPoints] = useState(0);
  const [streak, setStreak] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [level, setLevel] = useState(1);
  const [startTime, setStartTime] = useState<number>(0);
  const [studySession, setStudySession] = useState<StudySession | null>(null);

  // Load available levels
  const availableLevels = getGradeLevels();
  const categoriesForLevel = selectedLevel ? getCategoriesForGrade(selectedLevel) : [];

  // Level up effect
  useEffect(() => {
    const newLevel = Math.floor(points / 100) + 1;
    if (newLevel > level) {
      setLevel(newLevel);
    }
  }, [points, level]);

  // Select a grade level and start session
  const selectLevel = useCallback((levelNum: number) => {
    const levelCards = getCardsByGradeLevel(levelNum);
    
    if (levelCards.length === 0) {
      alert(`No cards available for Level ${levelNum} yet. Try another level!`);
      return;
    }

    console.log(`Loading ${levelCards.length} cards for level ${levelNum}`);
    
    setSelectedLevel(levelNum);
    setCurrentCards(levelCards);
    setCurrentIndex(0);
    setShowingAnswer(false);
    setShowingMoreInfo(false);
    setStartTime(Date.now());
    
    const session = createStudySession(levelNum, categoriesForLevel.map(c => c.id));
    setStudySession(session);
  }, [getCardsByGradeLevel, createStudySession, categoriesForLevel]);

  // Handle answer selection
  const handleAnswerClick = useCallback((selectedOption: string) => {
    if (!currentCards[currentIndex] || !studySession) return;
    
    const currentCard = currentCards[currentIndex];
    const isCorrect = selectedOption === currentCard.answer;
    const responseTime = Date.now() - startTime;
    
    updateCardStatistics(currentCard.id, isCorrect, responseTime);
    updateStudySession(studySession, currentCard.id, isCorrect, responseTime);
    
    if (isCorrect) {
      setPoints(prev => prev + 10);
      setStreak(prev => prev + 1);
      setCorrectAnswers(prev => prev + 1);
    } else {
      setStreak(0);
    }
    
    setTotalAnswered(prev => prev + 1);
    setShowingAnswer(true);

    setTimeout(() => {
      if (isCorrect) {
        alert("🎉 Correct! Great job!");
      } else {
        alert(`❌ Not quite! The correct answer is: ${currentCard.answer}`);
      }
    }, 100);
    
    setStartTime(Date.now());
  }, [currentCards, currentIndex, studySession, updateCardStatistics, updateStudySession, startTime]);

  // Navigation methods
  const nextCard = useCallback(() => {
    if (currentIndex < currentCards.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setShowingAnswer(false);
      setShowingMoreInfo(false);
      setStartTime(Date.now());
    } else if (studySession) {
      const completedSession = endStudySession(studySession);
      console.log('Study session completed:', completedSession);
    }
  }, [currentIndex, currentCards.length, studySession, endStudySession]);

  const previousCard = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setShowingAnswer(false);
      setShowingMoreInfo(false);
      setStartTime(Date.now());
    }
  }, [currentIndex]);

  const toggleMoreInfo = useCallback(() => {
    setShowingMoreInfo(prev => !prev);
    if (!showingMoreInfo) {
      setPoints(prev => prev + 3);
    }
  }, [showingMoreInfo]);

  const resetToLevelSelection = useCallback(() => {
    if (studySession) {
      endStudySession(studySession);
    }
    
    setSelectedLevel(null);
    setCurrentCards([]);
    setCurrentIndex(0);
    setShowingAnswer(false);
    setShowingMoreInfo(false);
    setStudySession(null);
  }, [studySession, endStudySession]);

  // Helper functions
  const getStreakMessage = useCallback(() => {
    if (streak >= 15) return "🔥 GRAMMAR MASTER! 🔥";
    if (streak >= 10) return "⚡ SUPER STREAK! ⚡";
    if (streak >= 5) return "🌟 Great Streak! 🌟";
    if (streak >= 3) return "✨ Nice Going! ✨";
    return "";
  }, [streak]);

  const getAccuracy = useCallback(() => {
    return totalAnswered > 0 ? Math.round((correctAnswers / totalAnswered) * 100) : 0;
  }, [totalAnswered, correctAnswers]);

  // Loading state
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-icon">📚</div>
          <div className="loading-title">Loading Grammar Cards...</div>
          <div className="loading-subtitle">
            Preparing your learning adventure!
          </div>
          {overallStats.totalCards > 0 && (
            <div className="loading-stats">
              {overallStats.totalCards} cards available
            </div>
          )}
        </div>
      </div>
    );
  }

  // Error state
  if (error || !isReady) {
    return (
      <div className="error-container">
        <div className="error-icon">❌</div>
        <div className="error-title">Oops! Something went wrong:</div>
        <div className="error-message">
          {error || 'Grammar cards are not ready yet.'}
        </div>
        <div className="error-buttons">
          <button
            onClick={() => window.location.reload()}
            className="grammar-btn grammar-btn-secondary"
          >
            🔄 Reload Page
          </button>
          <button
            onClick={onNavigateHome}
            className="grammar-btn grammar-btn-home"
          >
            🏠 Go Home
          </button>
        </div>
      </div>
    );
  }

  // Level Selection Screen
  if (!selectedLevel) {
    return (
      <div className="grammar-flashcard-container">
        {/* Stats Header */}
        <div className="stats-header">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">⭐ {points}</div>
              <div className="stat-label">Points</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">🔥 {streak}</div>
              <div className="stat-label">Streak</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">🎯 {getAccuracy()}%</div>
              <div className="stat-label">Accuracy</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">🏆 {level}</div>
              <div className="stat-label">Level</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">📚 {overallStats.totalCards}</div>
              <div className="stat-label">Total Cards</div>
            </div>
          </div>
          {getStreakMessage() && (
            <div className="streak-message">
              {getStreakMessage()}
            </div>
          )}
        </div>

        <h1 className="grammar-title">📝 Grammar Heroes 📝</h1>
        <p className="grammar-subtitle">Master the Building Blocks of Language!</p>
        <p className="grammar-description">Choose your skill level and start your grammar adventure</p>

        {/* Level Cards */}
        <div className="level-cards-grid">
          {availableLevels.map(levelData => {
            const cardCount = getCardsByGradeLevel(levelData.gradeLevel).length;
            return (
              <div
                key={levelData.gradeLevel}
                onClick={() => cardCount > 0 ? selectLevel(levelData.gradeLevel) : undefined}
                className={`level-card ${cardCount > 0 ? 'available' : 'unavailable'}`}
                style={{ borderColor: levelData.color }}
              >
                <div className="level-icon">{levelData.icon}</div>
                <h3 className="level-title">Grade {levelData.gradeLevel}</h3>
                <h4 className="level-name">{levelData.name}</h4>
                <p className="level-description">{levelData.description}</p>
                <p className="level-cards-count">
                  {cardCount > 0 ? `${cardCount} challenges await!` : 'Coming soon!'}
                </p>
                {levelData.topics && (
                  <div className="level-topics">
                    Topics: {levelData.topics.slice(0, 2).join(', ')}
                    {levelData.topics.length > 2 && '...'}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="home-button-container">
          <button onClick={onNavigateHome} className="grammar-btn grammar-btn-home">
            🏠 Return to Home Base
          </button>
        </div>
      </div>
    );
  }

  // Quiz Interface
  const currentCard = currentCards[currentIndex];
  const progressPercentage = ((currentIndex + 1) / currentCards.length) * 100;

  return (
    <div className="grammar-flashcard-container quiz-mode">
      {/* Header with Stats */}
      <div className="quiz-header">
        <div className="quiz-info">
          <h2>Level {selectedLevel} Grammar</h2>
          <div className="question-counter">
            Question {currentIndex + 1} of {currentCards.length}
          </div>
          {studySession && (
            <div className="session-accuracy">
              Session Accuracy: {studySession.totalAnswers > 0 ? Math.round((studySession.correctAnswers / studySession.totalAnswers) * 100) : 0}%
            </div>
          )}
        </div>
        
        <div className="quiz-stats">
          <div className="quiz-stat">
            <div className="quiz-stat-number">⭐ {points}</div>
            <div className="quiz-stat-label">Points</div>
          </div>
          <div className="quiz-stat">
            <div className="quiz-stat-number">🔥 {streak}</div>
            <div className="quiz-stat-label">Streak</div>
          </div>
          <div className="quiz-stat">
            <div className="quiz-stat-number">🎯 {getAccuracy()}%</div>
            <div className="quiz-stat-label">Accuracy</div>
          </div>
          <div className="quiz-stat">
            <div className="quiz-stat-number">🏆 {level}</div>
            <div className="quiz-stat-label">Level</div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="progress-bar-container">
        <div 
          className="progress-bar-fill" 
          style={{ width: `${progressPercentage}%` }}
        />
      </div>

      {/* Question Card */}
      <div className="grammar-flashcard">
        {/* Card indicators */}
        <div className="card-number">
          {currentIndex + 1}/{currentCards.length}
        </div>

        <div className="difficulty-indicator">
          {[1, 2, 3, 4, 5].map(i => (
            <div
              key={i}
              className={`difficulty-dot ${i <= currentCard.difficulty ? 'active' : ''}`}
            />
          ))}
        </div>

        <h3 className="question-text">{currentCard.question}</h3>

        {/* Category and tags */}
        <div className="card-tags">
          <span className="category-tag">
            {currentCard.category.replace('_', ' ').toUpperCase()}
          </span>
          {currentCard.subcategory && (
            <span className="subcategory-tag">
              {currentCard.subcategory.replace('_', ' ')}
            </span>
          )}
        </div>

        {/* Answer Options */}
        {!showingAnswer && (
          <div className="grammar-options">
            {currentCard.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswerClick(option)}
                className={`grammar-btn grammar-btn-option-${String.fromCharCode(97 + index)}`}
              >
                {option}
              </button>
            ))}
          </div>
        )}

        {/* Answer Revealed */}
        {showingAnswer && (
          <div className="answer-revealed">
            ✅ Correct Answer: {currentCard.answer}
          </div>
        )}

        {/* More Info Section */}
        {showingMoreInfo && (
          <div className="more-info">
            <h4>💡 Explanation:</h4>
            <p>{currentCard.moreInfo}</p>
            {currentCard.tags.length > 0 && (
              <div className="card-tags-list">
                <strong>Tags:</strong> {currentCard.tags.join(', ')}
              </div>
            )}
          </div>
        )}

        {/* Info Button */}
        <button
          onClick={toggleMoreInfo}
          className={`grammar-btn ${showingMoreInfo ? 'grammar-btn-primary' : 'grammar-btn-secondary'}`}
        >
          {showingMoreInfo ? '✅ Info Shown (+3 pts)' : '💡 Show Explanation'}
        </button>
      </div>

      {/* Navigation Controls */}
      <div className="navigation-controls">
        <button
          onClick={previousCard}
          disabled={currentIndex === 0}
          className="grammar-btn grammar-btn-previous"
        >
          ⬅️ Previous
        </button>
        
        <button
          onClick={nextCard}
          disabled={currentIndex === currentCards.length - 1}
          className="grammar-btn grammar-btn-next"
        >
          Next ➡️
        </button>
      </div>

      {/* Bottom Navigation */}
      <div className="bottom-navigation">
        <button
          onClick={resetToLevelSelection}
          className="grammar-btn grammar-btn-secondary"
        >
          🔄 Change Level
        </button>
        
        <button
          onClick={onNavigateHome}
          className="grammar-btn grammar-btn-home"
        >
          🏠 Home Base
        </button>
      </div>

      {/* Completion Celebration */}
      {currentIndex === currentCards.length - 1 && showingAnswer && (
        <div className="completion-modal">
          <div className="completion-content">
            <div className="completion-icon">🎉</div>
            <h2>Congratulations!</h2>
            <p>You've completed all Level {selectedLevel} grammar challenges!</p>
            <div className="final-stats">
              <div>Final Score: ⭐ {points} points</div>
              <div>Accuracy: 🎯 {getAccuracy()}%</div>
              {studySession && (
                <div>Session Time: ⏱️ {Math.round((Date.now() - new Date(studySession.startTime).getTime()) / 60000)} minutes</div>
              )}
            </div>
            <div className="completion-buttons">
              <button
                onClick={resetToLevelSelection}
                className="grammar-btn grammar-btn-secondary"
              >
                🔄 Try Another Level
              </button>
              <button
                onClick={onNavigateHome}
                className="grammar-btn grammar-btn-home"
              >
                🏠 Go Home
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GrammarFlashcards;